import { useEffect, useRef } from 'react';

/**
 * Hook für automatische Session-Ablauf nach Inaktivität mit Vorwarnung
 * @param {number} timeoutMinutes - Timeout in Minuten (Standard: 60)
 * @param {function} onTimeout - Callback-Funktion die aufgerufen wird wenn Session abläuft
 * @param {function} onWarning - Callback-Funktion für Vorwarnung (optional)
 * @param {number} warningSeconds - Sekunden vor Ablauf, wann gewarnt werden soll (Standard: 300)
 */
export const useSessionTimeout = (timeoutMinutes = 60, onTimeout, onWarning, warningSeconds = 300) => {
  useEffect(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) {
      return;
    }

    let timeoutId;
    let warningId;
    let lastActivity = Date.now();
    let isPageVisible = true;
    let pageHiddenTime = null;

    const resetTimeout = () => {
      lastActivity = Date.now();
      if (timeoutId) clearTimeout(timeoutId);
      if (warningId) clearTimeout(warningId);

      // Vorwarnung setzen
      if (onWarning && warningSeconds > 0 && warningSeconds < timeoutMinutes * 60) {
        warningId = setTimeout(() => {
          onWarning();
        }, (timeoutMinutes * 60 - warningSeconds) * 1000);
      }

      // Haupt-Timeout setzen
      timeoutId = setTimeout(() => {
        console.log(`Session-Timeout nach ${timeoutMinutes} Minuten Inaktivität`);
        onTimeout();
      }, timeoutMinutes * 60 * 1000);
    };

    const handleActivity = () => {
      resetTimeout();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPageVisible = false;
        pageHiddenTime = Date.now();
        console.log('Seite ist versteckt - Session-Tracking pausiert');
      } else {
        isPageVisible = true;
        if (pageHiddenTime) {
          const hiddenDuration = Date.now() - pageHiddenTime;
          const hiddenMinutes = hiddenDuration / (1000 * 60);
          console.log(`Seite war ${hiddenMinutes.toFixed(1)} Minuten versteckt`);
          const sessionData = loadSessionData();
          if (sessionData && sessionData.loginTime) {
            const sessionAge = Date.now() - sessionData.loginTime;
            const maxSessionAge = timeoutMinutes * 60 * 1000;
            if (sessionAge >= maxSessionAge) {
              console.log('Session ist während der Versteckzeit abgelaufen');
              onTimeout();
              return;
            }
          }
          pageHiddenTime = null;
        }
        resetTimeout();
      }
    };

    const handleBeforeUnload = () => {
      console.log('Seite wird geschlossen - Session-Daten werden gespeichert');
      const sessionData = loadSessionData();
      if (sessionData) {
        sessionData.lastActivity = Date.now();
        localStorage.setItem('sessionData', JSON.stringify(sessionData));
      }
    };

    const events = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'focus', 'input'
    ];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    resetTimeout();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (warningId) clearTimeout(warningId);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [timeoutMinutes, onTimeout, onWarning, warningSeconds]);
};

/**
 * Hook für erweiterte Session-Überwachung mit User-Status-Prüfung
 * @param {number} checkIntervalMinutes - Intervall für Status-Prüfung in Minuten (Standard: 120 = 2 Stunden)
 * @param {function} onUserDeactivated - Callback wenn User deaktiviert wurde
 * @param {function} onTeamDeactivated - Callback wenn Team deaktiviert wurde
 */
export const useExtendedSessionMonitoring = (checkIntervalMinutes = 120, onUserDeactivated, onTeamDeactivated) => {
  useEffect(() => {
    if (!checkIntervalMinutes || checkIntervalMinutes <= 0) {
      return;
    }

    let checkIntervalId;

    const checkUserAndTeamStatus = async () => {
      try {
        const sessionData = loadSessionData();
        if (!sessionData || !sessionData.user) {
          return;
        }

        // API-Aufruf zum Prüfen des User-Status
        const response = await fetch('/api/api.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'checkUserStatus',
            userId: sessionData.user.id
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.success) {
            // Prüfen ob User noch aktiv ist
            if (data.user && data.user.active === 0) {
              console.log('User wurde deaktiviert - automatisches Ausloggen');
              if (onUserDeactivated) {
                onUserDeactivated(data.user);
              }
              return;
            }

            // Prüfen ob Team noch aktiv ist (falls User ein Team hat)
            if (data.user && data.user.team_id && data.team && data.team.active === 0) {
              console.log('Team wurde deaktiviert - automatisches Ausloggen');
              if (onTeamDeactivated) {
                onTeamDeactivated(data.team);
              }
              return;
            }

            // Session-Daten aktualisieren falls sich etwas geändert hat
            if (data.user) {
              const updatedSessionData = {
                ...sessionData,
                user: { ...sessionData.user, ...data.user }
              };
              saveSessionData(updatedSessionData.user, updatedSessionData.loginTime);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Prüfen des User-Status:', error);
      }
    };

    // Erste Prüfung nach dem Intervall
    checkIntervalId = setTimeout(checkUserAndTeamStatus, checkIntervalMinutes * 60 * 1000);

    return () => {
      if (checkIntervalId) clearTimeout(checkIntervalId);
    };
  }, [checkIntervalMinutes, onUserDeactivated, onTeamDeactivated]);
};

/**
 * Utility-Funktion um Session-Daten zu speichern
 * @param {object} userData - Benutzerdaten
 * @param {number} loginTime - Login-Zeitstempel
 */
export const saveSessionData = (userData, loginTime = Date.now()) => {
  try {
    const sessionData = {
      user: userData,
      loginTime: loginTime,
      lastActivity: loginTime
    };
    localStorage.setItem('sessionData', JSON.stringify(sessionData));
    localStorage.setItem('user', JSON.stringify(userData)); // Für Kompatibilität
  } catch (error) {
    console.error('Fehler beim Speichern der Session-Daten:', error);
  }
};

/**
 * Utility-Funktion um Session-Daten zu laden
 * @returns {object|null} Session-Daten oder null
 */
export const loadSessionData = () => {
  try {
    const sessionData = localStorage.getItem('sessionData');
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    console.error('Fehler beim Laden der Session-Daten:', error);
    return null;
  }
};

/**
 * Utility-Funktion um die lastActivity in den Session-Daten zu aktualisieren
 */
export const updateLastActivity = () => {
  try {
    const sessionData = loadSessionData();
    if (sessionData && sessionData.user) {
      sessionData.lastActivity = Date.now();
      localStorage.setItem('sessionData', JSON.stringify(sessionData));
    }
  } catch (error) {
    console.error('Fehler beim Aktualisieren der lastActivity:', error);
  }
};

/**
 * Utility-Funktion um Session-Daten zu löschen
 */
export const clearSessionData = () => {
  try {
    localStorage.removeItem('sessionData');
    localStorage.removeItem('user'); // Für Kompatibilität
  } catch (error) {
    console.error('Fehler beim Löschen der Session-Daten:', error);
  }
};

/**
 * Utility-Funktion um zu prüfen ob Session noch gültig ist
 * @param {number} maxAgeMinutes - Maximale Session-Dauer in Minuten
 * @returns {boolean} true wenn Session gültig ist
 */
export const isSessionValid = (maxAgeMinutes = 60) => {
  try {
    const sessionData = loadSessionData();
    if (!sessionData || !sessionData.loginTime) {
      return false;
    }

    const now = Date.now();
    const sessionAge = now - sessionData.loginTime;
    const maxAgeMs = maxAgeMinutes * 60 * 1000;

    return sessionAge < maxAgeMs;
  } catch (error) {
    console.error('Fehler beim Prüfen der Session-Gültigkeit:', error);
    return false;
  }
};

/**
 * Utility-Funktion um zu prüfen ob Session während der Versteckzeit abgelaufen ist
 * @param {number} maxAgeMinutes - Maximale Session-Dauer in Minuten
 * @returns {boolean} true wenn Session während Versteckzeit abgelaufen ist
 */
export const isSessionExpiredWhileHidden = (maxAgeMinutes = 60) => {
  try {
    const sessionData = loadSessionData();
    if (!sessionData || !sessionData.loginTime) {
      return false;
    }

    const now = Date.now();
    const sessionAge = now - sessionData.loginTime;
    const maxAgeMs = maxAgeMinutes * 60 * 1000;

    // Wenn Session älter als maxAge ist, ist sie abgelaufen
    return sessionAge >= maxAgeMs;
  } catch (error) {
    console.error('Fehler beim Prüfen der Session-Gültigkeit:', error);
    return false;
  }
};

/**
 * Utility-Funktion um Session-Daten beim Login zu initialisieren
 * @param {object} userData - Benutzerdaten vom Login
 */
export const initializeSession = (userData) => {
  const loginTime = Date.now();
  saveSessionData(userData, loginTime);
  console.log('Session initialisiert:', { loginTime, user: userData.email });
};

/**
 * Utility-Funktion um Session-Daten beim Logout zu bereinigen
 */
export const cleanupSession = () => {
  clearSessionData();
  console.log('Session bereinigt');
};

/**
 * Utility-Funktion um zu prüfen ob ein Benutzer beim Laden der Seite automatisch ausgeloggt werden soll
 * @param {number} maxAgeMinutes - Maximale Session-Dauer in Minuten
 * @returns {boolean} true wenn Benutzer ausgeloggt werden soll
 */
export const shouldAutoLogout = (maxAgeMinutes = 60) => {
  try {
    // Zuerst prüfen, ob ein user-Eintrag vorhanden ist
    let user = localStorage.getItem('user');
    
    // Falls kein user-Eintrag vorhanden ist, versuchen wir es mit sessionData
    if (!user) {
      const sessionData = loadSessionData();
      if (sessionData && sessionData.user) {
        // User aus sessionData wiederherstellen
        localStorage.setItem('user', JSON.stringify(sessionData.user));
        user = JSON.stringify(sessionData.user);
      }
    }
    
    if (!user) {
      return false; // Kein Benutzer angemeldet
    }

    const sessionData = loadSessionData();
    if (!sessionData || !sessionData.loginTime) {
      return true; // Keine Session-Daten, ausloggen
    }

    const now = Date.now();
    const sessionAge = now - sessionData.loginTime;
    const maxAgeMs = maxAgeMinutes * 60 * 1000;

    return sessionAge >= maxAgeMs;
  } catch (error) {
    console.error('Fehler beim Prüfen des Auto-Logouts:', error);
    return true; // Bei Fehler sicherheitshalber ausloggen
  }
}; 